import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, CheckCircle, AlertCircle, RefreshCw, ArrowLeftCircle } from 'react-feather';
import Swal from 'sweetalert2';
import { useGetPurchaseOrdersQuery, useReceiveStockMutation, useGetPurchaseOrderByIdQuery } from '../../../../store/services/inventoryApi';

const BRAND = "#d3178a";
const imageURL = "https://gobooze.co.za/api/images/"; // standard image URL prefix

const StockReceiptForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedPOId, setSelectedPOId] = useState(id || '');
    const [notes, setNotes] = useState('');
    const [receiveItems, setReceiveItems] = useState([]);

    // Fetch POs that are ready to be received
    const { data: allPOsData } = useGetPurchaseOrdersQuery({ limit: 100 });
    const [receiveStock, { isLoading: isSubmitting }] = useReceiveStockMutation();

    // Fetch details when PO selected to get fresh items state
    const { data: selectedPODetail, isFetching: isFetchingPO } = useGetPurchaseOrderByIdQuery(selectedPOId, { skip: !selectedPOId });

    // Populate Pending POs List
    const pendingPOs = useMemo(() => {
        const list = allPOsData?.data || allPOsData || [];
        return Array.isArray(list) ? list.filter(po => po.status === 'Ordered' || po.status === 'PartiallyReceived') : [];
    }, [allPOsData]);

    // Handle PO Selection & Pre-fill
    useEffect(() => {
        if (selectedPODetail) {
            const po = selectedPODetail.data || selectedPODetail;
            const formItems = (po.items || []).map(item => {
                const variant = item.productVariantId;
                return {
                    productVariantId: variant?._id || variant,
                    productName: item.productName || variant?.variantName || '—',
                    sku: item.sku || variant?.sku || '—',
                    orderedQty: item.orderedQty || 0,
                    receivedQty: item.receivedQty || 0,
                    quantity: 0, // Proposed receive qty (0 by default)
                    poUnitCost: item.unitCost || 0,
                    unitCost: item.unitCost || 0, // Proposed unit cost (default to PO cost)
                    variantImage: variant?.variantImage || null
                };
            });
            setReceiveItems(formItems);
            setNotes('');
        }
    }, [selectedPODetail]);

    useEffect(() => {
        if (id) {
            setSelectedPOId(id);
        }
    }, [id]);

    const handleAcceptAll = () => {
        const updated = receiveItems.map(item => ({
            ...item,
            quantity: Math.max(0, item.orderedQty - item.receivedQty)
        }));
        setReceiveItems(updated);
    };

    const handleResetAll = () => {
        const updated = receiveItems.map(item => ({
            ...item,
            quantity: 0
        }));
        setReceiveItems(updated);
    };

    const handleItemChange = (index, field, val) => {
        const updated = [...receiveItems];
        if (field === 'quantity') {
            updated[index].quantity = Math.max(0, parseInt(val) || 0);
        } else if (field === 'unitCost') {
            updated[index].unitCost = Math.max(0, parseFloat(val) || 0);
        }
        setReceiveItems(updated);
    };

    const hasValidationError = useMemo(() => {
        return receiveItems.some(item => {
            const remaining = item.orderedQty - item.receivedQty;
            return item.quantity > remaining;
        });
    }, [receiveItems]);

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (hasValidationError) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Some items exceed the remaining quantity to receive. Please fix these before submitting.',
                confirmButtonColor: BRAND
            });
            return;
        }

        const itemsToReceive = receiveItems
            .filter(i => i.quantity > 0)
            .map(i => ({
                productVariantId: i.productVariantId,
                productName: i.productName,
                sku: i.sku,
                quantity: i.quantity,
                poUnitCost: i.poUnitCost,
                unitCost: i.unitCost
            }));

        if (itemsToReceive.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Items Entered',
                text: 'Please enter at least one item quantity to receive.',
                confirmButtonColor: BRAND
            });
            return;
        }

        const po = selectedPODetail?.data || selectedPODetail;
        const payload = {
            poId: selectedPOId,
            supplierId: po?.supplierId?._id || po?.supplierId,
            storeId: po?.storeId?._id || po?.storeId,
            notes: notes,
            items: itemsToReceive
        };

        try {
            await receiveStock(payload).unwrap();
            Swal.fire({
                icon: 'success',
                title: 'Stock Received Successfully',
                text: 'The PO status and destination store stock have been updated.',
                confirmButtonColor: BRAND
            });
            navigate(id ? `/inventory/purchase-orders/${id}` : '/inventory/purchase-orders');
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Receipt Failed',
                text: error?.data?.message || 'Failed to process stock receipt.',
                confirmButtonColor: BRAND
            });
        }
    };

    const po = selectedPODetail?.data || selectedPODetail;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Receive Stock" parent="Inventory" title="New Receipt" />
            <Container fluid={true} className="pb-5">
                <Row>
                    <Col sm="12">
                        {/* Dropdown Section to choose PO if not coming from URL ID */}
                        {!id && (
                            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-5 max-w-xl">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Purchase Order</label>
                                <select
                                    value={selectedPOId}
                                    onChange={(e) => setSelectedPOId(e.target.value)}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                >
                                    <option value="">-- Select Pending PO --</option>
                                    {pendingPOs.map(p => (
                                        <option key={p._id} value={p._id}>
                                            {p.poNumber} - {p.supplierId?.name || 'Unknown Supplier'} ({p.status})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {isFetchingPO && (
                            <div className="text-center p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
                                <Spinner color="primary" className="mb-2" />
                                <p className="text-sm text-gray-500">Loading Purchase Order details...</p>
                            </div>
                        )}

                        {selectedPOId && !isFetchingPO && po && (
                            <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl mx-auto flex flex-col overflow-hidden border border-gray-100">
                                {/* Header (Mirroring Transfer modal style) */}
                                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Receive stock from PO</h3>
                                        <p className="text-xs text-gray-400 mt-1">
                                            PO Ref: <span className="font-semibold text-gray-700">{po.poNumber}</span> | Supplier: <span className="font-semibold text-gray-700">{po.supplierId?.name || '—'}</span> | Store: <span className="font-semibold text-gray-700">{po.storeId?.storeName || '—'}</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleAcceptAll}
                                            className="text-xs font-semibold px-3 py-2 rounded-lg border transition-colors hover:bg-emerald-100"
                                            style={{ color: "#15803d", borderColor: "#bbf7d0", background: "#dcfce7" }}
                                        >
                                            Accept all remaining
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleResetAll}
                                            className="text-xs font-semibold px-3 py-2 rounded-lg border transition-colors hover:bg-gray-100"
                                            style={{ color: "#4b5563", borderColor: "#e5e7eb", background: "#f3f4f6" }}
                                        >
                                            Reset quantities
                                        </button>
                                    </div>
                                </div>

                                <form onSubmit={onSubmit} className="flex-1 flex flex-col">
                                    {/* Notes / Ref text area */}
                                    <div className="px-6 pt-4 pb-2">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Notes / GRN Reference</label>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            rows={2}
                                            placeholder="e.g. Delivery Note Number, courier reference, receiving logs..."
                                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        />
                                    </div>

                                    {/* Items Table */}
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gray-50 border-b border-gray-100">
                                                    <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider">Product</th>
                                                    <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">SKU</th>
                                                    <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">Prev / Ordered</th>
                                                    <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">Receive Now</th>
                                                    <th className="px-4 py-3 text-right text-[10px] font-bold text-gray-400 uppercase tracking-wider">PO Unit Price</th>
                                                    <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">Recv Unit Price</th>
                                                    <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">Price Diff (%)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {receiveItems.map((item, index) => {
                                                    const remaining = item.orderedQty - item.receivedQty;
                                                    const isOver = item.quantity > remaining;
                                                    const isValidQty = item.quantity > 0 && !isOver;

                                                    // Calculate price difference
                                                    const originalPrice = item.poUnitCost;
                                                    const currentPrice = item.unitCost;
                                                    let diffBadge = null;
                                                    if (originalPrice > 0) {
                                                        const diff = currentPrice - originalPrice;
                                                        const pct = (diff / originalPrice) * 100;
                                                        if (pct > 0) {
                                                            diffBadge = <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">+{pct.toFixed(2)}%</span>;
                                                        } else if (pct < 0) {
                                                            diffBadge = <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700">{pct.toFixed(2)}%</span>;
                                                        } else {
                                                            diffBadge = <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-500">0.00%</span>;
                                                        }
                                                    } else {
                                                        diffBadge = <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-400">N/A</span>;
                                                    }

                                                    return (
                                                        <tr key={index} className={isOver ? "bg-red-50/50" : ""}>
                                                            {/* Product Image & Name */}
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-3">
                                                                    {item.variantImage
                                                                        ? <img
                                                                            src={`${imageURL}${item.variantImage}`}
                                                                            className="w-10 h-10 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                                                                            alt=""
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                        : <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-100">
                                                                            <Package size={14} className="text-gray-300" />
                                                                        </div>
                                                                    }
                                                                    <div>
                                                                        <p className="font-semibold text-gray-800 text-sm leading-snug">{item.productName}</p>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* SKU */}
                                                            <td className="px-4 py-4 text-center text-xs font-mono text-gray-500">
                                                                {item.sku}
                                                            </td>

                                                            {/* Prev / Ordered */}
                                                            <td className="px-4 py-4 text-center">
                                                                <div className="flex flex-col items-center gap-0.5">
                                                                    <span className="text-sm font-medium text-gray-700">{item.receivedQty} received</span>
                                                                    <span className="text-xs text-gray-400">of {item.orderedQty} ordered</span>
                                                                </div>
                                                            </td>

                                                            {/* Receive Now input */}
                                                            <td className="px-4 py-4 text-center">
                                                                <div className="inline-flex flex-col items-center">
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        value={item.quantity}
                                                                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                                        className="w-24 border rounded-xl px-2.5 py-1.5 text-sm text-center font-semibold focus:outline-none transition-all shadow-sm"
                                                                        style={{
                                                                            borderColor: isOver ? "#dc2626" : isValidQty ? "#bbf7d0" : "#d1d5db",
                                                                            background: isOver ? "#fee2e2" : isValidQty ? "#f0fdf4" : "#ffffff"
                                                                        }}
                                                                    />
                                                                    {isOver && (
                                                                        <span className="text-[10px] text-red-600 font-semibold mt-1 flex items-center gap-0.5 justify-center">
                                                                            <AlertCircle size={9} /> Exceeds remaining ({remaining})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* PO Unit Price */}
                                                            <td className="px-4 py-4 text-right font-medium text-gray-600">
                                                                ${originalPrice.toFixed(2)}
                                                            </td>

                                                            {/* Recv Unit Price input */}
                                                            <td className="px-4 py-4 text-center">
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min={0}
                                                                    value={item.unitCost}
                                                                    onChange={(e) => handleItemChange(index, 'unitCost', e.target.value)}
                                                                    className="w-24 border border-gray-200 rounded-xl px-2.5 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-pink-500 font-medium"
                                                                />
                                                            </td>

                                                            {/* Price Difference Badge */}
                                                            <td className="px-4 py-4 text-center">
                                                                {diffBadge}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Footer Section */}
                                    <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 mt-4">
                                        <button
                                            type="button"
                                            onClick={() => navigate(id ? `/inventory/purchase-orders/${id}` : '/inventory/purchase-orders')}
                                            className="inline-flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 bg-white px-4 py-2.5 rounded-xl hover:bg-gray-50 hover:border-gray-300 font-medium shadow-sm transition-colors"
                                        >
                                            <ArrowLeftCircle size={15} />
                                            Back
                                        </button>
                                        
                                        <button
                                            type="submit"
                                            disabled={isSubmitting || hasValidationError}
                                            className="inline-flex items-center gap-2 text-white text-sm font-semibold px-6 py-2.5 rounded-xl disabled:opacity-50 transition-colors shadow-md shadow-emerald-700/20"
                                            style={{ background: "#15803d" }}
                                        >
                                            {isSubmitting ? <RefreshCw size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                                            Process Receipt
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StockReceiptForm;
